import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { refreshSession, setAccessToken, setUnauthorizedHandler } from "../api/client";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [activeShopId, setActiveShopId] = useState(null);
  const [initializing, setInitializing] = useState(true);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setActiveShopId(null);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(clearSession);
  }, [clearSession]);

  const loadCurrentUser = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    setActiveShopId((prev) =>
      prev && data.shops.some((s) => s.shop_id === prev) ? prev : data.shops[0]?.shop_id ?? null
    );
    return data;
  }, []);

  useEffect(() => {
    // On first load, try to establish a session from the refresh cookie
    // (e.g. the user still has a valid session from a previous visit).
    (async () => {
      try {
        const { data } = await refreshSession();
        setAccessToken(data.access_token);
        await loadCurrentUser();
      } catch (err) {
        clearSession();
      } finally {
        setInitializing(false);
      }
    })();
  }, [loadCurrentUser, clearSession]);

  const login = useCallback(
    async (email, password) => {
      const { data } = await api.post("/auth/login", { email, password });
      setAccessToken(data.access_token);
      await loadCurrentUser();
    },
    [loadCurrentUser]
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const changePassword = useCallback(
    async (currentPassword, newPassword) => {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      // Backend revokes all refresh tokens on password change, so the
      // session is already dead server-side - clear it locally too, with a
      // short delay so the caller can show a success message first.
      setTimeout(clearSession, 1500);
    },
    [clearSession]
  );

  const activeShop = useMemo(
    () => (user?.shops ?? []).find((s) => s.shop_id === activeShopId) ?? null,
    [user, activeShopId]
  );

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      initializing,
      shops: user?.shops ?? [],
      activeShop,
      setActiveShopId,
      login,
      logout,
      changePassword,
    }),
    [user, initializing, activeShop, login, logout, changePassword]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
